import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6bmimqff40lgbk6chb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = (value: string) => {
    if (value.length <= 500) {
      setFormData({ ...formData, message: value });
      setCharCount(value.length);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="animate-slide-in-left">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-6 font-display">
              Get in Touch
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              All our electricians are NAPIT Approved and hold the latest 18th Edition certification. We provide a 24/7 emergency electrician service throughout Bridgend and South Wales.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-map-pin-fill text-primary text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Address</h4>
                  <p className="text-gray-600 text-sm">
                    35 Dwynwen Road
                    <br />
                    Broadlands, Bridgend
                    <br />
                    CF31 5FD
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-phone-fill text-primary text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Phone</h4>
                  <div className="space-y-1">
                    <div>
                      <span className="text-gray-500 text-xs font-semibold">Landline:</span>
                      <a
                        href="tel:01656497474"
                        className="text-gray-600 hover:text-primary transition-colors text-sm cursor-pointer ml-2"
                      >
                        01656 497474
                      </a>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs font-semibold">Mobile:</span>
                      <a
                        href="tel:+447403546489"
                        className="text-gray-600 hover:text-primary transition-colors text-sm cursor-pointer ml-2"
                      >
                        07403 546489
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-mail-fill text-primary text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Email</h4>
                  <a
                    href="mailto:mega-lec@outlook.com"
                    className="text-gray-600 hover:text-primary transition-colors text-sm cursor-pointer"
                  >
                    mega-lec@outlook.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 h-64 rounded-xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2482.5!2d-3.5767!3d51.5045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDMwJzE2LjIiTiAzwrAzNCczNi4xIlc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bridgend Electricians Location"
              ></iframe>
            </div>
          </div>

          <div className="animate-slide-in-right">
            <div className="bg-gray-50 rounded-2xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-secondary mb-6 font-display">
                Request a Free Quote
              </h3>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-primary font-semibold text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 font-semibold text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </p>
                </div>
              )}

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                      placeholder="07403 546489"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Required *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="Rewiring">Rewiring</option>
                    <option value="Consumer Unit Upgrade">Consumer Unit Upgrade</option>
                    <option value="EV Charger Installation">EV Charger Installation</option>
                    <option value="EICR Testing">EICR Testing</option>
                    <option value="Emergency Electrician">Emergency Electrician</option>
                    <option value="Lighting Installation">Lighting Installation</option>
                    <option value="Fault Finding">Fault Finding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => handleMessageChange(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-sm"
                    placeholder="Please provide any additional details about your electrical needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Request Free Quote
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
